import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import ScientificPattern from "@/components/ScientificPattern";

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-white" data-testid="hero-section">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1628595351029-c2bf17511435?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Molecular research background"
          className="w-full h-full object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white" />
        <ScientificPattern variant="hex" className="opacity-60" />
      </div>

      <div className="absolute top-24 left-10 h-24 w-24 rounded-full bg-brandblue-400/20 blur-2xl animate-float-slow hidden md:block" />
      <div className="absolute bottom-24 right-16 h-32 w-32 rounded-full bg-brandblue-600/10 blur-2xl animate-float-slower hidden md:block" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[420px] bg-radial-blue-glow opacity-80 pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-28 md:py-36 text-center">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-blue-200 rounded-full px-4 py-1.5 mb-8 shadow-blue-glow-sm"
          data-testid="hero-tagline-badge"
        >
          <FlaskConical className="h-3.5 w-3.5 text-brandblue-600" strokeWidth={1.5} />
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-brandblue-700">Advancing Research Through Precision</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight font-medium text-slate-900 font-display"
          data-testid="hero-headline"
        >
          Precision <span className="text-gradient-blue">Research Peptides</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed"
          data-testid="hero-subheadline"
        >
          Premium Research Use Only compounds manufactured to rigorous quality standards with comprehensive batch documentation and independent laboratory verification.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/shop">
            <Button size="lg" className="rounded-full btn-lift px-8 py-6 text-sm" data-testid="hero-browse-btn">
              Browse Research Compounds <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
          <Link to="/lab-testing">
            <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-sm border-slate-300" data-testid="hero-lab-testing-btn">
              View Laboratory Testing
            </Button>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-14 grid grid-cols-3 max-w-2xl mx-auto divide-x divide-blue-100"
          data-testid="hero-stats-row"
        >
          <div className="px-4" data-testid="hero-stat-purity">
            <p className="text-3xl md:text-4xl font-display font-semibold text-gradient-blue tracking-tight">99%+</p>
            <p className="mt-1 text-[11px] md:text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Purity Standard</p>
          </div>
          <div className="px-4" data-testid="hero-stat-tested">
            <p className="text-3xl md:text-4xl font-display font-semibold text-slate-900 tracking-tight">100%</p>
            <p className="mt-1 text-[11px] md:text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Batch Lab Tested</p>
          </div>
          <div className="px-4" data-testid="hero-stat-compounds">
            <p className="text-3xl md:text-4xl font-display font-semibold text-slate-900 tracking-tight">12+</p>
            <p className="mt-1 text-[11px] md:text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Research Compounds</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
